import { Injectable } from '@angular/core';
import { InterfaceService } from '@app/interfaces';
import { HttpClient, HttpHeaders,HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, of } from 'rxjs';
import {ICsAgGridJs, ICsGridApi} from "@app/shared/cs-ag-grid-js/cs-ag-grid-js.interface";
import {TranslateService} from "@ngx-translate/core";
import {ICsGridActionCellRenderer} from "@app/shared/cs-ag-grid-js/cs-ag-grid-cell-renderers/cs-ag-grid-cell-renderers.interface";
import { debounceTime, switchMap, map, tap, catchError } from "rxjs/operators";
import * as moment from "moment";
import {DatePipe} from "@app/pipes";
import {TimelineDatePipe} from "@app/pipes/timeline-date.pipe";
import {SettingsService} from "@app/core";
import {TimelineService} from "@app/shared/services/timeline.service";
import {CommonService} from "@app/services";

@Injectable()

export class ScheduledActivityService extends InterfaceService {
    /** UserColumnPreferences screen key for saved grid sort (field + order). */
    static readonly SCHEDULE_ACTIVITY_SORT_SCREEN = 'schedule_activity_sort';

    private gridInstance: ICsGridApi;
    usersList: any;
    viewOpportunityAccess:boolean = false;
    freemiumAccess:boolean = false;
    public filterActionEnabled = true;
    RunThroughScheduledActivitiesAccess :boolean = false;
    isOldJobView = false;
    currentUser: any;

    private static readonly LAST_COMM_DEBOUNCE_MS = 1000;
    private lastCommRequest$ = new Subject<{
        tableType: number;
        tablePKID: number;
        recordType: string;
        diaryId: number;
        lastCommunicationContainerId: string;
        lastVisitContainerId: string;
    }>();

    constructor(private http: HttpClient,
                private translate: TranslateService,
                private ShortDatePipe: DatePipe,
                private TimelineDatePipe: TimelineDatePipe,
                private settingsService: SettingsService,
                private timelineService: TimelineService,
                private commonService: CommonService) {
        super();
        this.lastCommRequest$
            .pipe(
                debounceTime(ScheduledActivityService.LAST_COMM_DEBOUNCE_MS),
                switchMap((params) =>
                    this.http.get(this.getApiUrl(
                        `lastCommunication?tableType=${params.tableType}&tablePKID=${params.tablePKID}&type=${params.recordType}&diaryId=${params.diaryId}`
                    )).pipe(
                        map((res: any) => {
                            let lastCommunication = null;
                            let lastVisit = null;
                            if (res != null) {
                                if (res.lastCommunication !== undefined) {
                                    lastCommunication = res.lastCommunication;
                                } else if (res.lastCommuncation !== undefined) {
                                    lastCommunication = res.lastCommuncation;
                                }
                                if (res.lastVisit !== undefined) {
                                    lastVisit = res.lastVisit;
                                }
                            }
                            return {
                                lastCommunication: lastCommunication,
                                lastVisit: lastVisit,
                                lastCommunicationContainerId: params.lastCommunicationContainerId,
                                lastVisitContainerId: params.lastVisitContainerId
                            };
                        }),
                        catchError(() => of({
                            lastCommunication: null,
                            lastVisit: null,
                            lastCommunicationContainerId: params.lastCommunicationContainerId,
                            lastVisitContainerId: params.lastVisitContainerId
                        }))
                    )
                )
            )
            .subscribe({
                next: ({ lastCommunication, lastVisit, lastCommunicationContainerId, lastVisitContainerId }) => {
                    const container = document.getElementById(lastCommunicationContainerId);
                    const lastVisitContainer = document.getElementById(lastVisitContainerId);
                    if (container) {
                        container.innerHTML = this.buildLastCommunicationHTML(lastCommunication);
                    }
                    if (lastVisitContainer) {
                        lastVisitContainer.innerHTML = this.buildLastVisitHTML(lastVisit);
                    }
                },
                error: () => {}
            });

        this.commonService.$currentUser.subscribe( data => {
            this.currentUser = data;
        });
    }
    private filterData = new BehaviorSubject('');
    filterData$ = this.filterData.asObservable();

    private filterParams = new BehaviorSubject('');
    filterParams$ = this.filterParams.asObservable();

    private refreshActivityList = new BehaviorSubject(null);
    $refreshActivityList = this.refreshActivityList.asObservable();

    setFilterData(data) {
        this.filterData.next(data)
    }
    setFilterParams(data){
        this.filterParams.next(data)
    }
    setRefreshActivityList(data){
        this.refreshActivityList.next(data)
    }

    getScheduledActivityData() {
        let url = this.getApiUrl(`sales/getScheduledActivities`);
        return this.http.get(url);
    }
    getScheduledEmail(oppid){
        let url = this.getApiUrl(`sales/checkTodayEmailActivity/${oppid}`);
        return this.http.get(url);
    }

    getScheduledActivityList(data) {
        let url = this.getApiUrl(data['url'], data['params']);
        return this.http.get(url);
    }
    getScheduledActivities() {
        let url = this.getApiUrl( 'getScheduledActivitiesData');
        return this.http.get(url);
    }
    getFilterDatas(data){
        let url = this.getApiUrl(`getScheduledActivitiesData?assignee=${data.assignee}&date=${data.date}&searchText=${data.searchText}&limit=${data.pageLimit}&page=${data.currentPage}&order=${data.order}&field=${data.field}&type=${data.type}&recordType=${data.recordType}`)
        return this.http.get(url);
    }

    getScheduleActivitySortPreference(): Observable<{ field: string; order: string } | null> {
        const url = this.getApiUrl(
            'get_user_screen_preference/' + encodeURIComponent(ScheduledActivityService.SCHEDULE_ACTIVITY_SORT_SCREEN)
        );
        return this.http.get<any>(url).pipe(
            map((res) => {
                if (!res || typeof res !== 'object') {
                    return null; // never saved — caller falls back to its own hardcoded default
                }
                const field = res.field != null ? String(res.field) : '';
                const order = res.order != null ? String(res.order) : '';
                if (!field || !order) {
                    // Explicitly saved as unsorted (user reset the column) — distinct from "never saved".
                    return { field: '', order: '' };
                }
                const normalizedOrder = order.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
                return { field, order: normalizedOrder };
            })
        );
    }

    saveScheduleActivitySortPreference(field: string, order: string): Observable<any> {
        const hasSort = !!field && !!order;
        const normalizedOrder = hasSort ? (String(order).toUpperCase() === 'ASC' ? 'ASC' : 'DESC') : '';
        const url = this.getApiUrl('userColumnOrder');
        const body = {
            columnValue: JSON.stringify({ field: hasSort ? field : '', order: normalizedOrder }),
            screenName: ScheduledActivityService.SCHEDULE_ACTIVITY_SORT_SCREEN,
        };
        return this.http.post(url, body);
    }

    completeScheduledActivity(data:object){
        let url = this.getApiUrl(`completeScheduledActivity`);
        return this.http.post(url,data);
    }

    getActivityList = (data: any): Observable<{ data: any[]; count: number }> => {
        // Derived purely from the live AG-Grid sort model (never from the previous request's
        // field/order) so an explicit user reset — an empty sortModel — is sent as unsorted
        // rather than silently resending the last-known sort.
        let order = '';
        let field = '';
        if (data.sort) {
            try {
                const sortModel = typeof data.sort === 'string' ? JSON.parse(data.sort) : data.sort;
                if (sortModel && (sortModel.colId != null || sortModel.field != null)) {
                    field = sortModel.colId || sortModel.field;
                    const sortDir = (sortModel.sort || '').toString().toLowerCase();
                    order = sortDir === 'desc' ? 'DESC' : 'ASC';
                }
            } catch {
                // malformed sort payload — treat as unsorted
            }
        }
        // The grid's colId for this column is 'timeToShow' (row-data property used for rendering),
        // but getScheduledActivitiesDataAction special-cases that name for a different screen's
        // "last visit date/time" column and sorts by the joined (slow, unindexed) Diaries.fromdatetime.
        // This column is actually the due date/time, so always send the fast/correct backend field.
        if (field === 'timeToShow') {
            field = 'dateToShow';
        }
        const url = this.getApiUrl('getScheduledActivitiesData');
        const params = {
            date: '',
            searchText: data.searchText,
            limit: data.limit,
            page: data.page,
            order: order,
            field: field,
            type: Array.isArray(data.type) ? data.type.join(',') : data.type,
            recordType: data.recordType,
            filters:JSON.stringify(data.filters),
            selectedTab: data.activeTab,
            tagIds: data.tagIds || '',
        };

        return this.http.get<any>(url, { params }).pipe(
            map(response => ({
                data: response.scheduledActivities,
                count: response.count,
                params: params,
            }))
        );
    };

    setGridInstance(instance) {
        this.gridInstance = instance;
    }
    setViewOpportunityAccess(freemium,opportunity,RunThroughScheduledActivities){
        this.viewOpportunityAccess = opportunity;
        this.freemiumAccess = freemium;
        this.RunThroughScheduledActivitiesAccess = RunThroughScheduledActivities;
    }

    public getGridConfig(activeTab,openDeleteModal, openViewModal, openEditModal,runThrough,permissions,usersList,appliedData,completeSelected,onContactClick,onDataLoad){
        this.usersList = usersList;
        let colDefs;
        let filterDefs;
        switch (activeTab) {
            case 'myActivity':
            case 'othersActivity':
            case 'ai-den':
            case 'unassignedActivity':
            case 'complete':
                colDefs = this.getMyActivityGridColumn(activeTab,openDeleteModal, openViewModal, openEditModal,permissions,onContactClick,appliedData);
                filterDefs = this.getMyActivityGridFilter(activeTab, runThrough,permissions,appliedData);
                break;
        }
        const gridConfig: ICsAgGridJs = {
            hideHeaderPanel: true,
            filterPanelConfig: filterDefs,
            gridConfig: {
                enableDetailGrid: false,
                rowsPerPage: 10,
                rowSelection: activeTab != 'ai-den',
                hideHeaderRowSelection: true,
                colDefs: colDefs,
                dataResKeys: { dataKey: 'data', countKey: 'count'},
                onRowSelection: (event,data)=>completeSelected(event,data),
                gridList: (params: any) => {
                    const reqParams = params['reqParams'] || {};
                    reqParams['filters'] = reqParams['filters'] ? JSON.parse(reqParams['filters']) : null;
                    if (reqParams['filters']) {
                        const isJobRecordType = reqParams['filters'].some((f: any) => f.ID == 'recordType' && f.value == 'job');
                        if (isJobRecordType && 'clientsconfig' in this.settingsService.getAllPermissions()['accessDetails'] && 'NewJobView' in this.settingsService.getAllPermissions()['accessDetails'].clientsconfig && this.settingsService.getAllPermissions()['accessDetails'].clientsconfig.NewJobView == false) {
                            this.isOldJobView = true;
                        } else {
                            this.isOldJobView = false;
                        }
                    }
                    return this.getActivityList({
                        ...appliedData,
                        ...reqParams,
                        activeTab,
                    }).pipe(
                        tap((res) => {
                            // Notify the component whenever data arrives
                            this.filterActionEnabled = !!(res.data.length);
                            if (onDataLoad) onDataLoad(res);
                        })
                    );
                }

            }
        };
        return gridConfig;
    }

    getMyActivityGridColumn(activeTab,openDeleteModal, openViewModal, openEditModal,permissions,onContactClick,appliedData?){
        const dueDateSort = appliedData && appliedData.order
            ? (String(appliedData.order).toUpperCase() === 'ASC' ? 'asc' : 'desc')
            : null;
        return [
            {
                field: 'activityType',
                headerName: '',
                maxWidth: 50,
                minWidth: 50,
                resizable: true,
                cellRenderer: (params) => {
                    const data = params.data || {};
                    const content = data.activityType;
                    if (!content) {
                        return `<span>-</span>`;
                    } else if (content == 'Call'){
                        return `<span style="display: flex; justify-content: center; align-items: center; height: 100%;"><i class="fa-solid fa-phone"></i></span>`;
                    } else if (content == 'Email') {
                        return `<span style="display: flex; justify-content: center; align-items: center; height: 100%;"><i class="fa fa-envelope"></i></span>`;
                    }else if (content == 'Meeting'){
                        return `<span style="display: flex; justify-content: center; align-items: center; height: 100%;"><i class="fa-solid fa-handshake"></i></span>`;
                    }else if (content == 'Task'){
                        return `<span style="display: flex; justify-content: center; align-items: center; height: 100%;"><i class="fa-solid fa-briefcase"></i></span>`;
                    }else if (content == 'Service reminder'){
                        return `<span style="display: flex; justify-content: center; align-items: center; height: 100%;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <g clip-path="url(#clip0_9399_20295)">
                            <path d="M7.33084 3.22231C7.10527 2.67741 6.51057 2.38738 5.94223 2.54265L3.36422 3.24575C2.85448 3.38636 2.5 3.84923 2.5 4.37655C2.5 11.6243 8.37669 17.501 15.6244 17.501C16.1517 17.501 16.6146 17.1465 16.7552 16.6368L17.4583 14.0587C17.6136 13.4904 17.3236 12.8957 16.7787 12.6701L13.9663 11.4983C13.4888 11.2991 12.9351 11.4368 12.6099 11.8381L11.4264 13.2824C9.36396 12.3069 7.69411 10.637 6.71856 8.57461L8.16284 7.394C8.56419 7.06589 8.70188 6.51513 8.50267 6.03761L7.33084 3.22524V3.22231Z" fill="url(#paint0_linear_9399_20295)"/>
                            <path d="M13.7944 2.27065C14.0436 1.54148 15.0511 1.5194 15.3465 2.2044L15.3715 2.27107L15.7077 3.2544C15.7848 3.47992 15.9093 3.68628 16.0729 3.85958C16.2365 4.03288 16.4353 4.16907 16.6561 4.25899L16.7465 4.29274L17.7298 4.62857C18.459 4.87774 18.4811 5.88524 17.7965 6.18065L17.7298 6.20565L16.7465 6.5419C16.5209 6.61891 16.3144 6.74341 16.1411 6.90701C15.9677 7.07061 15.8314 7.26949 15.7415 7.49024L15.7077 7.58024L15.3719 8.56399C15.1227 9.29315 14.1152 9.31524 13.8202 8.63065L13.7944 8.56399L13.4585 7.58065C13.3815 7.35506 13.257 7.14861 13.0934 6.97524C12.9298 6.80187 12.731 6.66561 12.5102 6.57565L12.4202 6.5419L11.4369 6.20607C10.7073 5.9569 10.6852 4.9494 11.3702 4.6544L11.4369 4.62857L12.4202 4.29274C12.6457 4.21568 12.8521 4.09115 13.0254 3.92756C13.1987 3.76396 13.3349 3.56511 13.4248 3.3444L13.4585 3.2544L13.7944 2.27065ZM17.9165 0.833984C17.9944 0.833984 18.0708 0.85585 18.1369 0.897098C18.2031 0.938345 18.2563 0.997319 18.2906 1.06732L18.3106 1.11607L18.4565 1.54357L18.8844 1.6894C18.9625 1.71594 19.031 1.76507 19.0811 1.83057C19.1313 1.89607 19.1609 1.97499 19.1662 2.05733C19.1714 2.13966 19.1521 2.22171 19.1107 2.29306C19.0693 2.36442 19.0077 2.42188 18.9336 2.45815L18.8844 2.47815L18.4569 2.62398L18.3111 3.0519C18.2845 3.13 18.2353 3.19844 18.1698 3.24857C18.1042 3.29869 18.0253 3.32823 17.943 3.33345C17.8607 3.33867 17.7786 3.31934 17.7073 3.27789C17.636 3.23645 17.5785 3.17476 17.5423 3.10065L17.5223 3.0519L17.3765 2.6244L16.9486 2.47857C16.8704 2.45203 16.802 2.4029 16.7518 2.3374C16.7016 2.2719 16.672 2.19298 16.6668 2.11064C16.6615 2.02831 16.6808 1.94626 16.7222 1.87491C16.7636 1.80355 16.8253 1.74609 16.8994 1.70982L16.9486 1.68982L17.3761 1.54398L17.5219 1.11607C17.55 1.03375 17.6031 0.96228 17.6739 0.911692C17.7447 0.861105 17.8295 0.833932 17.9165 0.833984Z" fill="url(#paint1_linear_9399_20295)"/>
                        </g>
                        <defs>
                            <linearGradient id="paint0_linear_9399_20295" x1="2.5" y1="12.251" x2="17.4994" y2="12.1568" gradientUnits="userSpaceOnUse">
                                <stop stop-color="#80216E"/>
                                <stop offset="1" stop-color="#D67C39"/>
                            </linearGradient>
                            <linearGradient id="paint1_linear_9399_20295" x1="10.8726" y1="6.22509" x2="19.1667" y2="6.17304" gradientUnits="userSpaceOnUse">
                                <stop stop-color="#80216E"/>
                                <stop offset="1" stop-color="#D67C39"/>
                            </linearGradient>
                            <clipPath id="clip0_9399_20295">
                                <rect width="20" height="20" fill="white" transform="translate(0 0.000976562)"/>
                            </clipPath>
                        </defs>
                    </svg></span>`;
                    }
                }
            },
            {
                field: 'customerDetails',
                headerName: this.translate.instant('Description'),
                minWidth: 270,
                resizable: true,
                wrapText: true,
                autoHeight: true,
                cellRenderer: (params) => {
                    const data = params.data || {};
                    const content = data.description;
                    const tags = data.savedTags || [];

                    if (!content) {
                        return `<span>-</span>`;
                    } else {
                        let tagsHtml = '';
                        if (tags.length > 0) {
                            tagsHtml = tags.map(tag => `<span style="padding:4px 5px 2px; margin-right:8px; border-radius:2px; font-size:12px;border: 1px solid var(--Base-structure-Stroke, #E1E1E1);background: var(--Background-Light-BG, #FAFAFA);">${tag.description}</span>`).join('');
                        }
                        const wrap =
                            'min-width:0; white-space: normal; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word;';
                        const spacer = tags.length ? '<span style="height:6px;display:block"></span>' : '';
                        return `<div style="${wrap}"><span style="${wrap}">${content}</span>${spacer}<span style="display:flex;flex-wrap:wrap;gap:6px;">${tagsHtml}</span></div>`;
                    }

                }
            },
            {
                field: 'timeToShow',
                sortable: true,
                sort: dueDateSort,
                headerName: this.translate.instant('Due date/time'),
                minWidth: 310,
                resizable: true,
                cellRenderer: (params) => {
                    const data = params.data || {};
                    const content = data.timeToShow;
                    const dateLine = content ? this.ModifyActivityDateTime(content) : '';
                    const badgeHtml = activeTab === 'complete' ? '' : this.buildMyActivityStatusBadgeHtml(data.dateToShow);
                    const rowStyle =
                        'display:flex;flex-flow:row nowrap;align-items:center;justify-content:space-between;gap:8px;width:100%;min-width:0;overflow:hidden;box-sizing:border-box;';
                    if (!dateLine && !badgeHtml) {
                        return `<div style="${rowStyle}"><span>-</span></div>`;
                    }
                    const leftHtml = dateLine
                        ? `<div style="min-width:0;flex:1 1 0%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${dateLine}</div>`
                        : `<div style="min-width:0;flex:1 1 0%;">-</div>`;
                    return `<div style="${rowStyle}">${leftHtml}${badgeHtml}</div>`;
                }
            },
            {
                field: 'customer name',
                headerName: this.translate.instant('Customer name'),
                minWidth: 160,
                resizable: true,
                cellRenderer: (params) => {
                    const data = params.data || {};
                    const content = data.customerDetails.customerName;
                    if (!content) {
                        return `<span>-</span>`;
                    } else {
                        return `<span ${this.truncateStyle(content)}>${content}</span>`;
                    }
                }
            },
            {
                field: 'Phone',
                headerName: this.translate.instant('Project.Customer.Contact'),
                minWidth: 260,
                resizable: true,
                cellRenderer: (params) => {
                    const data = params.data || {};
                    const customer = data.customerDetails || {};
                    const mobile = customer.customerMobileNumber;
                    const telephone = customer.customerTelephoneNumber;
                    const email = customer.customerEmail;
                    if (!mobile && !telephone && !email) {
                        return `<span>-</span>`;
                    }
                    // Build the HTML – fixed-width icon wrapper so phone/mobile/email align
                    let html = '';
                    const iconWrap = 'style="display: inline-block; width: 20px; text-align: center; vertical-align: middle;"';
                    const truncateContact = (val: string) => `style="display: inline-block; max-width: 190px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #3883c1; cursor: pointer; vertical-align: middle;" title="${this.escapeTitle(val)}"`;
                    if (telephone) {
                        html += `<span class="clickable-phone" data-type="telephone" style="display: flex; align-items: center; gap: 4px;"><span ${iconWrap}><i class="fa-solid fa-phone"></i></span><span ${truncateContact(telephone)}>${telephone}</span></span>`;
                    }
                    if (mobile) {
                        html += `<span class="clickable-phone" data-type="mobile" style="display: flex; align-items: center; gap: 4px;"><span ${iconWrap}><i class="fa-solid fa-mobile-screen-button"></i></span><span ${truncateContact(mobile)}>${mobile}</span></span>`;
                    }
                    if (email) {
                        html += `<span class="clickable-phone" data-type="email" style="display: flex; align-items: center; gap: 4px;"><span ${iconWrap}><i class="fa-solid fa-envelope"></i></span><span ${truncateContact(email)}>${email}</span></span>`;
                    }
                    // Create a wrapper element to attach events
                    const wrapper = document.createElement('div');
                    wrapper.innerHTML = html;
                    // Attach click events
                    wrapper.querySelectorAll('.clickable-phone').forEach(el => {
                        el.addEventListener('click', () => {
                            const type = el.getAttribute('data-type');
                            onContactClick(data, type);
                        });
                    });
                    return wrapper;
                }
            },
            {
                field: 'Object',
                headerName: this.translate.instant('Object'),
                minWidth: 140,
                resizable: true,
                cellRenderer: 'customPopOverRenderer',
                cellRendererParams: {
                    popUpDetail: (data: any) => this.buildPopupHTML(data),
                    activity: true
                }
            },
            {
                field: 'site name/address',
                headerName: this.translate.instant('Site name/address'),
                minWidth: 260,
                resizable: true,
                cellRenderer: (params) => {
                    const data = params.data || {};
                    const content = data.siteContactDetails.siteContactAddress;
                    if (!content) {
                        return `<span>-</span>`;
                    } else {
                        return `<span ${this.truncateStyle(content)}>${content}</span>`;
                    }
                }
            },
            {
                field: 'assignees',
                headerName: this.translate.instant('Assigned'),
                minWidth: 160,
                width: 160,
                resizable: false,
                cellRenderer:'customUserRenderer'
            },
            {
                field: 'action',
                headerName: this.translate.instant('Action'),
                minWidth: 160,
                width: 160,
                cellRenderer: 'actionRenderer',
                pinned: 'right',
                hide: activeTab == 'ai-den',
                cellRendererParams: {
                    actions: [
                        {
                            id: 'view',
                            text: 'View',
                            onClick: (data) => openViewModal(data),
                            hide: (data) => {
                                if (data.recordType === 'Opportunity') {
                                    return !(this.viewOpportunityAccess && (!data.jmsOnly || this.freemiumAccess));
                                } else if (data.recordType === 'customer' || data.recordType === 'contract' || data.recordType === 'job') {
                                    return false;
                                }
                                return true;
                            },
                        },
                        {
                            id: 'edit_requested',
                            text: 'Edit',
                            onClick: (data) => openEditModal(data),
                            hide: () => !permissions.update,
                        },
                        {
                            id: 'Delete',
                            text: 'Delete',
                            onClick: (data) => openDeleteModal(data),
                            hide: () => !permissions.delete,
                        }
                    ]
                } as ICsGridActionCellRenderer
            }
        ];
    }

    private valOrDash(val: any): string {
        return val !== undefined && val !== null && val !== '' ? val : '-';
    }

    /** Escape string for use in HTML title attribute (tooltip). */
    private escapeTitle(val: string): string {
        if (val == null) return '';
        return String(val)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    /** Status pill for My activity grid: shown on the right inside the Due date/time cell. */
    private buildMyActivityStatusBadgeHtml(dateToShow: any): string {
        if (!dateToShow) {
            return '';
        }
        const dateValue = this.ModifyActivityDate(dateToShow).label;
        const bgColor = dateValue === 'Overdue' ? '#BE6D6D' : '#477596';
        const icon = dateValue === 'Overdue' ? '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
            '<path d="M7.05108 1.33219C7.36508 1.12419 7.89708 0.975686 8.29858 1.37719L10.6221 3.70119C11.0251 4.10319 10.8761 4.63519 10.6676 4.94869C10.5547 5.11868 10.4078 5.26339 10.2361 5.37369C10.0691 5.48019 9.85808 5.56069 9.63058 5.54869C9.52051 5.54089 9.41077 5.52905 9.30158 5.51319L9.26758 5.50819C9.15027 5.49137 9.03234 5.47919 8.91408 5.47169C8.66208 5.45919 8.56508 5.50169 8.53408 5.53169L7.28908 6.77719C7.24908 6.81719 7.19908 6.90619 7.16108 7.07719C7.12458 7.24219 7.10858 7.44519 7.10458 7.67019C7.10108 7.88619 7.10858 8.10719 7.11658 8.32019L7.11708 8.34369C7.12458 8.55519 7.13208 8.77119 7.12158 8.94069C7.08908 9.45619 6.68758 9.83569 6.29258 10.0112C5.89758 10.1862 5.33408 10.2297 4.94258 9.83769L3.81758 8.71269L1.76508 10.7652C1.73075 10.802 1.68935 10.8316 1.64335 10.8521C1.59735 10.8726 1.54769 10.8836 1.49734 10.8845C1.44699 10.8854 1.39697 10.8761 1.35028 10.8572C1.30358 10.8384 1.26117 10.8103 1.22556 10.7747C1.18995 10.7391 1.16188 10.6967 1.14302 10.65C1.12415 10.6033 1.11489 10.5533 1.11578 10.5029C1.11667 10.4526 1.12769 10.4029 1.14819 10.3569C1.16868 10.3109 1.19823 10.2695 1.23508 10.2352L3.28708 8.18269L2.16208 7.05769C1.77058 6.66569 1.81358 6.10269 1.98908 5.70769C2.16408 5.31269 2.54408 4.91119 3.05908 4.87869C3.22908 4.86819 3.44508 4.87569 3.65658 4.88319L3.68008 4.88369C3.89308 4.89119 4.11408 4.89919 4.33008 4.89569C4.55508 4.89169 4.75808 4.87569 4.92308 4.83919C5.09408 4.80119 5.18308 4.75069 5.22308 4.71069L6.46808 3.46569C6.49858 3.43519 6.54108 3.33769 6.52808 3.08569C6.52057 2.96742 6.50839 2.84949 6.49158 2.73219L6.48708 2.69819C6.47121 2.58899 6.45937 2.47925 6.45158 2.36919C6.43908 2.14169 6.51958 1.93069 6.62558 1.76369C6.73358 1.59369 6.88308 1.44369 7.05108 1.33219Z" fill="white"/>\n' +
            '</svg>\n' : '';
        const safeTitle = this.escapeTitle(dateValue);
        const escaped = this.escapeTitle(dateValue);
        return `<span style="display:inline-flex;flex-shrink:0;align-items:center;min-width:0;max-width:42%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${safeTitle}"><span style="background-color: ${bgColor}; color: white; padding: 2px 6px; border-radius: 2px; display:inline-flex; align-items:center; gap:4px; min-width:0; max-width:100%; overflow:hidden;"><span style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap; min-width:0;">${escaped}</span></span></span>`;
    }

    /** Inline style for truncating text with ellipsis + optional title for full text tooltip. */
    private truncateStyle(title?: string): string {
        const base = 'display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
        if (title) {
            return `style="${base}" title="${this.escapeTitle(title)}"`;
        }
        return `style="${base}"`;
    }

    private normalizeVisitDate(dateValue: any): string | null {
        if (!dateValue) {
            return null;
        }
        if (typeof dateValue === 'string') {
            return dateValue.replace(/\.\d+$/, '');
        }
        if (typeof dateValue === 'object' && dateValue.date) {
            return String(dateValue.date).replace(/\.\d+$/, '');
        }
        return null;
    }

    private buildLastCommunicationHTML(lastCommunication: any): string {
        if (!lastCommunication) {
            return `
    <div class="flex-display pt-5">
            There are no communications
    </div>
    `;
        }

        if (lastCommunication.emailId) {
            let fromName = '';
            if (lastCommunication.createdBy) {
                fromName = lastCommunication.isUserDeleted
                    ? lastCommunication.companyName
                    : lastCommunication.createdBy;
            } else {
                fromName = lastCommunication.mailFromName;
            }
            return `
    <div class="flex-display pt-5">
        <div class="w-8"><i class="fa fa-envelope last-com-icon"></i></div>
        <div class="w-92 last-communication">
            <span>${this.valOrDash(this.timelineService.convertTimeLineDateTime(lastCommunication.dateToShow))}</span>
            <div class="pt-3"><b>From:</b> ${this.valOrDash(fromName)}</div>
            <div class="pt-3"><b>To:</b> ${this.valOrDash(lastCommunication.mailToName)}</div>
            <div class="pt-3 message"><b class="mr-4">Subject:</b>${this.valOrDash(lastCommunication.subject)}</div>
        </div>
    </div>
    `;
        }

        if (lastCommunication.smsId) {
            return `
    <div class="flex-display pt-5">
        <div class="w-8"><i class="fas fa-sms last-com-icon"></i></div>
        <div class="w-92 last-communication">
            <span>${this.valOrDash(this.timelineService.convertTimeLineDateTime(lastCommunication.dateToShow))}</span>
            <div class="pt-3"><b>To:</b> ${this.valOrDash(lastCommunication.smsContactName)}</div>
            <div class="pt-3 message"><b class="mr-4">Message:</b>${this.valOrDash(lastCommunication.message)}</div>
        </div>
    </div>
    `;
        }

        if (lastCommunication.phoneId) {
            return `
    <div class="flex-display pt-5">
    <div class="w-8"><i class="fa fa-phone last-com-icon"></i></div>
        <div class="w-92 last-communication">
            <span>${this.valOrDash(this.timelineService.convertTimeLineDateTime(lastCommunication.dateToShow))}</span>
            <div class="pt-3"><b>To:</b> ${this.valOrDash(lastCommunication.contactName)}</div>
            <div class="pt-3 message"><b class="mr-4">Message:</b>${this.valOrDash(lastCommunication.phoneCallNote)}</div>
        </div>
    </div>
    `;
        }

        if (lastCommunication.printId) {
            return `
    <div class="flex-display pt-5">
    <div class="w-8"><i class="fa fa-print last-com-icon"></i></div>
        <div class="w-92 last-communication">
            <span>${this.valOrDash(this.timelineService.convertTimeLineDateTime(lastCommunication.dateToShow))}</span>
            <div class="pt-3">${this.valOrDash(lastCommunication.printMessage)}</div>
        </div>
    </div>
    `;
        }

        return `
    <div class="flex-display pt-5">
            There are no communications
    </div>
    `;
    }

    private buildLastCommunicationLoadingHTML(): string {
        return `
                <div class="flex-display pt-5">
                    <div class="w-8">
                        <div class="animated-background" style="width: 16px; height: 12px;"></div>
                    </div>
                    <div class="w-92 last-communication">
                        <div class="text-layout animated-background" style="width: 200px; height: 12px;"></div>
                        <div class="text-layout animated-background" style="width: 200px; height: 12px; margin-top: 8px;"></div>
                        <div class="text-layout animated-background" style="width: 200px; height: 12px; margin-top: 8px;"></div>
                    </div>
                </div>
    `;
    }

    private buildLastVisitHTML(lastVisitData: any): string {
        const visit = Array.isArray(lastVisitData) ? lastVisitData[0] : lastVisitData;
        if (!visit) {
            return `<div>There are no diary events in the past</div>`;
        }

        const description = visit.Description !== undefined ? visit.Description : visit.description;
        const profilePicture = visit.profilePicture !== undefined ? visit.profilePicture : visit.ProfilePicture;
        const engineerName = visit.engineerName !== undefined ? visit.engineerName : visit.EngineerName;
        const fromDateValue = visit.job_id
            ? (visit.lastVisitedDate !== undefined ? visit.lastVisitedDate : visit.FromDateTime)
            : visit.fromDate;
        const fromDate = this.normalizeVisitDate(fromDateValue);
        const formattedDateValue = fromDate ? this.timelineService.convertTimeLineDateTime(fromDate, 'date') : '';
        const formattedDate = formattedDateValue === 'Invalid date' ? '' : formattedDateValue;
        const time = visit.time !== undefined ? visit.time : '';

        return `
                <div class="flex-display pt-5">
                    <div class="w-8">
                        <i class="fa fa-calendar-alt last-com-icon"></i>
                    </div>
                    <div class="w-92 last-communication">
                        <span>${this.valOrDash(description)}</span>

                        <div class="last-visit-row">
                                 ${profilePicture ? `
                                    <img class="profile-picture" src="${profilePicture}">
                                ` : `
                                    <img src="images/profile_avatar_small.jpg" alt="Avatar" class="profile-picture">
                                `}
                                 <span class="text">${this.valOrDash(engineerName)}</span>
                        </div>

                        <div class="last-visit-row">
                            <i class="fa fa-calendar-alt last-com-icon"></i>
                            <span class="text">
                                ${this.valOrDash(formattedDate)}
                                ${time ? `(${time})` : ''}
                             </span>
                        </div>
                    </div>
                </div>
            `;
    }

    private buildPopupHTML(data: any): string {
        const content = (data.recordType || '').toLowerCase();
        const lastCommunicationContainerId = `sched-last-comm-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
        const lastVisitContainerId = `sched-last-visit-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

        let html = `
      <div class="popup-wrapper-sched">
        <div class="arrow-left-sched"></div>
        <div class="popup-header-sched">
          ${content === 'job' ? 'Job information' : 'Opportunity information'}
        </div>
        <div class="popup-content-sched">
    `;

        if (content === 'job') {
            const color = data.stageName ? '#fff' : 'black';
            html += `
        <div class="row-sched"><strong>Description</strong> <br>${this.valOrDash(data.recordDescription)}</div>
        <div class="row-sched"><strong>Notes</strong> <br>${this.valOrDash(data.jobNotes)}</div>
        <div class="row-sched"><strong>Status</strong>
        <div class="mt-4">
          <span class="status-badge-sched" style="background: ${data.stageColor}; color: ${color}">${this.valOrDash(data.stageName)}</span>
      `;
            if (data.jobAssignedUsers && data.jobAssignedUsers.length > 0) {
                html += `<span class="fl-right">`;
                data.jobAssignedUsers.forEach((user: any, index: number) => {
                    if (index < 3) {
                        html += `
                        <img src="${user.profilepicture || 'images/profile_avatar_small.jpg'}" alt="Avatar" class="avatar-image">
                        `;
                    }

                })
                if (data.jobAssignedUsers && data.jobAssignedUsers.length > 3) {
                    html += `<span class="user-count">+${data.jobAssignedUsers.length - 3}</span>`;
                }
                html += '</span>';
            }
            html += `</div></div>`;
        }

        if (content === 'opportunity') {
            const lastComm = data.lastCommunication || {};
            const customer = data.customerDetails || {};
            const color = data.opportunityStatus ? '#fff' : 'black';

            html += `
        <div class="row-sched"><strong>Description</strong> <br> ${this.valOrDash(data.opportunityDescription)}</div>
        <div class="row-sched"><strong>Notes</strong> <br> ${this.valOrDash(data.opportunityNotes)}</div>
        <div class="row-sched" *ngIf="${this.freemiumAccess}"><strong>Sales person</strong> <br>
        ${data.salesPerson.salesPersonProfile && data.salesPerson.salesPersonProfile !== '' ? `<img src="${data.salesPerson.salesPersonProfile}" class="sales-person">` : `<img src="images/profile_avatar_small.jpg" class="sales-person">`}
        <span>${this.valOrDash(data.salesPerson.salesPersonName)}</span></div>
        <div class="row-sched"><strong>Status</strong> </br> <div class="popover-sub-content mt-4"><span class="status-text-style status-badge-sched label
      ${data.opportunityStatus === 'Rejected' ? 'label-important' : ''} ${data.opportunityStatus === 'Accepted' || data.opportunityStatus === 'Ongoing' ? 'label-success' : ''}"
      style="color: ${color}">
      ${this.valOrDash(data.opportunityStatus)}</span></div></div>`;
        }

        let tableType = data.recordTableType;
        let tablePKID = data.recordId;
        let recordType = data.recordType;
        let diaryId = data.diaryId;

        this.lastCommRequest$.next({
            tableType,
            tablePKID,
            recordType,
            diaryId,
            lastCommunicationContainerId,
            lastVisitContainerId
        });

        html += `
    <div class="border-top"></div>
    <div class="section-sched">
    <div class="popover-sub-title">
        Last communication
    </div>
    <div class="popover-sub-content" id="${lastCommunicationContainerId}">
`;

        html += this.buildLastCommunicationLoadingHTML();

        html += `
    </div>
</div>
`;

        html += `
        <div class="section-sched">
    <div class="popover-sub-title">
      Last visit
    </div>
        <div class="popover-sub-content" id="${lastVisitContainerId}">
            ${this.buildLastCommunicationLoadingHTML()}
    </div>
  </div>
      `;

        html += `</div></div>`;
        return html;
    }

    getMyActivityGridFilter(activeTab, runThrough, permissions,appliedData) {
        let filters = [];
        let actions = [];
        let appliedFilter = [];
        appliedFilter =  Array.isArray(appliedData.filters) ? appliedData.filters : [];

        switch (activeTab) {
            case 'myActivity':
            case 'unassignedActivity':
            case 'ai-den':
                filters = [
                    {
                        filterInterfaceLabel: 'Activity type',
                        ID: 'activityType',
                        filterType: 'staticMultiSelect',
                        filterOptions: [
                            { id: '1', text: 'Call' },
                            { id: '2', text: 'Email' },
                            { id: '3', text: 'Meeting' },
                            { id: '4', text: 'Task' },
                        ],
                    },
                    {
                        filterInterfaceLabel: 'Record type',
                        ID: 'recordType',
                        filterType: 'staticSingleSelect',
                        filterOptions: [
                            { id: 'all', text: "All" },
                            { id: 'customer', text: "Customer" },
                            { id: 'opportunity', text: "Opportunity" },
                            { id: 'contract', text: 'Contract' },
                            { id: 'job', text: 'Job' }
                        ],
                    },
                    {
                        filterInterfaceLabel: 'Due dates',
                        ID: 'dueDate',
                        filterType: 'staticSingleSelect',
                        filterOptions: [
                            { id: 'All dates', text: "All dates" },
                            { id: 'Today', text: "Due today" },
                            { id: 'Overdue', text: "Overdue" },
                            { id: 'Tomorrow', text: "Due tomorrow" },
                            { id: 'Week', text: "Due this week" },
                            { id: 'Next_week', text: "Due next week" },
                        ],
                    }
                ];

                // Add action only for these tabs
                if (activeTab !== 'ai-den') {
                    actions = [
                        {
                            id: 'runThrough',
                            text: `\u25BA ${this.translate.instant('run.scheduled.activities')}`,
                            onClick: (...args) => runThrough(...args),
                            actionTypeProps: {bgColour: '#746CA6'},
                            hide: () => !this.RunThroughScheduledActivitiesAccess,
                            disable: () => !this.filterActionEnabled || this.isOldJobView
                        },
                    ];
                }
                break;

            case 'othersActivity':
            case 'complete':
                filters = [
                    {
                        filterInterfaceLabel: 'Activity type',
                        ID: 'activityType',
                        filterType: 'staticMultiSelect',
                        filterOptions: [
                            { id: '1', text: 'Call' },
                            { id: '2', text: 'Email' },
                            { id: '3', text: 'Meeting' },
                            { id: '4', text: 'Task' },
                        ],
                    },
                    {
                        filterInterfaceLabel: 'Record type',
                        ID: 'recordType',
                        filterType: 'staticSingleSelect',
                        filterOptions: [
                            { id: 'all', text: "All" },
                            { id: 'customer', text: "Customer" },
                            { id: 'opportunity', text: "Opportunity" },
                            { id: 'contract', text: 'Contract' },
                            { id: 'job', text: 'Job' }
                        ],
                    },
                    {
                        filterInterfaceLabel: 'Due dates',
                        ID: 'dueDate',
                        filterType: 'staticSingleSelect',
                        filterOptions: [
                            { id: 'All dates', text: "All dates" },
                            { id: 'Today', text: "Due today" },
                            { id: 'Overdue', text: "Overdue" },
                            { id: 'Tomorrow', text: "Due tomorrow" },
                            { id: 'Week', text: "Due this week" },
                            { id: 'Next_week', text: "Due next week" },
                        ],
                    },
                    {
                        filterInterfaceLabel: 'Displayed by',
                        ID: 'assignee',
                        filterType: 'staticSingleSelect',
                        filterOptions: activeTab == 'othersActivity' ? this.usersList.filter((u: any) => u.id != this.currentUser.id) : this.usersList,
                    },
                ];

                // No actions for 'complete'
                if (activeTab !== 'complete') {
                    actions = [
                        {
                            id: 'runThrough',
                            text: `\u25BA ${this.translate.instant('run.scheduled.activities')}`,
                            onClick: (...args) => runThrough(...args),
                            actionTypeProps: { bgColour: '#746CA6' },
                            hide: () => !this.RunThroughScheduledActivitiesAccess,
                            disable: () => !this.filterActionEnabled || this.isOldJobView
                        },
                    ];
                }
                break;

            default:
                filters = [];
                actions = [];
                appliedFilter = [];
                break;
        }

        return { filters, actions, appliedFilter, showTagListDropDown: true };
    }

    ModifyActivityDate(date) {
        const mDate = moment(date);
        const today = moment().startOf('day');
        const tomorrow = today.clone().add(1, 'day');

        let tooltipDate = mDate.format('DD/MM/YYYY (HH:mm)');
        let label = '';

        // Past dates
        if (mDate.isBefore(today, 'day')) {
            label = 'Overdue';
        }
        // Today
        else if (mDate.isSame(today, 'day')) {
            label = 'Due today';
        }
        // Tomorrow
        else if (mDate.isSame(tomorrow, 'day')) {
            label = 'Due tomorrow';
        }
        else {
            // After tomorrow
            const diffDays = mDate.diff(today, 'days');
            const diffWeeks = mDate.diff(today, 'weeks');
            const diffMonths = mDate.diff(today, 'months');

            if (diffDays < 7) {
                label = `${diffDays} day${diffDays > 1 ? 's' : ''} left`;
            }
            else if (diffWeeks < 5) {
                label = `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} left`;
            }
            else {
                label = `${diffMonths} month${diffMonths > 1 ? 's' : ''} left`;
            }
        }

        return {
            label: label,
            tooltip: tooltipDate
        };
    }


    /**
     * Display string for the Due date/time column (Figma):
     * - Yesterday: "Yesterday on 9:00 AM" (time only)
     * - Today: "Due today at 3:15 pm"
     * - Tomorrow: "Due tomorrow at 9:01"
     * - 2–6 days from today: "Due on Thursday at 14:01" (weekday name)
     * - 7+ days ahead or older than yesterday: "Due on DD/MM/YY at …"
     */
    ModifyActivityDateTime(date) {
        const m = moment(date);
        if (!m.isValid()) {
            return '-';
        }
        const today = moment().startOf('day');
        const yesterday = today.clone().subtract(1, 'day');
        const tomorrow = today.clone().add(1, 'day');
        const eventDay = m.clone().startOf('day');

        const utcDate = moment.utc(date);
        const currentTime = this.TimelineDatePipe.transform(utcDate, 'datetime');
        const timeMatch = currentTime && currentTime.match(/\(([^)]+)\)/);
        const timeStr = timeMatch ? timeMatch[1] : '';
        const convertedDate = this.ShortDatePipe.transform(date);
        const dayName = m.format('dddd');

        if (eventDay.isSame(yesterday, 'day')) {
            return `Due yesterday on ${timeStr}`;
        }
        if (eventDay.isSame(today, 'day')) {
            return `Due today at ${timeStr}`;
        }
        if (eventDay.isSame(tomorrow, 'day')) {
            return `Due tomorrow at ${timeStr}`;
        }

        const diffDays = eventDay.diff(today, 'days');
        if (diffDays < 0) {
            return `Due on ${convertedDate} at ${timeStr}`;
        }
        if (diffDays >= 7) {
            return `Due on ${convertedDate} at ${timeStr}`;
        }
        return `Due on ${dayName} at ${timeStr}`;
    }

    getScheduledActivitiesCount(activeTab) {
        let url = this.getApiUrl( `getScheduledActivitiesCount?activeTab=${activeTab}`);
        return this.http.get(url);
    }


}
